import {formatReferenceCopy as formatDemoCopy} from 'utils/typography';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import cx from 'classnames';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Button from '@mui/material/Button';
import {Delete, Edit, ExpandMore, LinkPopover, LocalPolice, Search} from '@vedmarket/ui-kit';

import {COLORS} from '../../../../../../../config/constants';
import {
  useBaseCurrency,
  useGetBrokerServicesSearchByOrderIdMutation,
  useGetUserBrokerServicesByOrderIdQuery,
  useRemoveBrokerServicesByOrderIdMutation,
  useUserBrokerService,
} from '../../../../../../../api/calc';
import {useGetCalcIdFromRoute} from '../../../../../../../utils/hooks';
import {isCalculationViewModeError, numberWithSpaces} from '../../../../../../../utils/helpers';
import ModalBig from '../../../../../../common/modalBig';
import {readOnlyButtonEndIcon} from 'components/common/read-only-action-icon';
import 'components/common/read-only-action-button.scss';
import {useCalculationReadOnly} from '../../../../calculation-read-only';
import CompanyCard from '../company-card';
import ModalSearch, {SORTING_OPTIONS} from '../modalSearch';

import './broker-services.scss';

const BrokerServices = () => {
  const { t } = useTranslation();

  const orderId = useGetCalcIdFromRoute();
  const baseCurrency = useBaseCurrency();
  const readOnly = useCalculationReadOnly();
  const userBrokerService = useUserBrokerService();

  const {refetch: refetchGetUserBrokerServices} = useGetUserBrokerServicesByOrderIdQuery(orderId);
  const [ getBrokerServices, {data: companies} ] = useGetBrokerServicesSearchByOrderIdMutation();
  const [ removeBrokerServices ] = useRemoveBrokerServicesByOrderIdMutation();

  const [ shouldOpenModal, setShouldOpenModal ] = useState(false);

  const hasCompleted = Boolean(userBrokerService);
  const buttonLabel = hasCompleted
    ? t('calc__text__customs__findOtherBrokerServices')
    : t('calc__text__customs__findBrokerServices');

  const handleCloseModal = () => setShouldOpenModal(false);

  const handleChoose = () => {
    refetchGetUserBrokerServices();
    handleCloseModal();
  };

  const handleSearch = async () => {
    await getBrokerServices({
      orderId,
      currency: baseCurrency,
      order_by: SORTING_OPTIONS.COMPANIES_DESK,
    });
    setShouldOpenModal(true);
  };

  const handleRemove = async () => {
    const result = await removeBrokerServices(orderId);

    if (isCalculationViewModeError(result)) {
      return;
    }

    refetchGetUserBrokerServices();
  };

  return (
    <div className='broker-services'>
      <Accordion defaultExpanded sx={{
        marginBottom: 0,
        border: `1px solid ${COLORS.SLIGHTLY_GRAY}`,
        boxShadow: 'none',
        '&.Mui-expanded': {
          marginBottom: 0,
        },
      }}>
        <AccordionSummary expandIcon={<ExpandMore/>} sx={{
          padding: '24px',
          minHeight: '74px',
          '&.Mui-expanded': {
            minHeight: '74px',
            paddingBottom: '23px',
          },
          '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': {
            margin: 0,
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            color: COLORS.LIGHT_ACTION,
            marginLeft: '16px',
          },
          '& .MuiAccordionSummary-expandIconWrapper svg': {
            width: '24px',
            height: '24px',
          },
        }}>
          <div className='broker-services__title'>
            <div className='broker-services__name'>
              {hasCompleted
                ? <LocalPolice color={COLORS.GREEN} className='broker-services__icon'/>
                : <Edit color={COLORS.LIGHT_TEXT_DISABLED_OPACITY} className='broker-services__icon'/>}
              <Typography variant='h6' sx={{
                color: COLORS.BLACK_DARK,
                fontSize: '20px',
                fontWeight: 700,
                lineHeight: '26px',
                letterSpacing: 0,
              }}>
                {formatDemoCopy(t('calc__text__customs__brokerServices'))}
              </Typography>
            </div>
            {userBrokerService && <Typography variant='h6' sx={{
              color: COLORS.BLACK_DARK,
              fontSize: '20px',
              fontWeight: 400,
              lineHeight: '26px',
              letterSpacing: 0,
              flexShrink: 0,
            }}>
              {`${numberWithSpaces(userBrokerService.cost_total)} ${userBrokerService.currency}`}
            </Typography>}
          </div>
        </AccordionSummary>
        <AccordionDetails sx={{
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
          <Button
            variant='outlined'
            className={cx({'read-only-action-button': readOnly})}
            disabled={readOnly}
            startIcon={<Search/>}
            endIcon={readOnlyButtonEndIcon(readOnly)}
            fullWidth
            onClick={readOnly ? undefined : handleSearch}
            title={readOnly ? t('calc__message__lockedByApplication') : undefined}
          >
            <Typography component='span' variant='inherit'>{formatDemoCopy(buttonLabel)}</Typography>
          </Button>
          <LinkPopover
            linkText={t('calc__help__brokerChoiceLink')}
            text={formatDemoCopy(t('calc__help__brokerChoice'))}
          />
        </AccordionDetails>
      </Accordion>
      <ModalBig
        isOpen={shouldOpenModal}
        onClose={handleCloseModal}
        title={t('calc__text__customs__searchForBrokerageServices')}
        isSearch
      >
        {userBrokerService && <div className='broker-services__selection'>
          <Typography variant='h6' sx={{color: COLORS.BLACK_DARK}}>
            {formatDemoCopy(t('calc__text__delivery__yourChoice'))}
          </Typography>
          <CompanyCard isActive company={userBrokerService}/>
          <Button
            variant='outlined'
            className={cx({'read-only-action-button': readOnly})}
            disabled={readOnly}
            startIcon={<Delete/>}
            fullWidth
            onClick={readOnly ? undefined : handleRemove}
            title={readOnly ? t('calc__message__lockedByApplication') : undefined}
          >
            <Typography component='span' variant='inherit'>
              {formatDemoCopy(t('calc__text__customs__removeTheCustomsBroker'))}
            </Typography>
          </Button>
        </div>}
        <ModalSearch handleChoose={handleChoose} preloadCompanies={companies}/>
      </ModalBig>
    </div>
  );
};

export default BrokerServices;
